import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { MultiValue, GroupBase } from 'react-select';
import {
  InterfaceNameValue,
  InterfaceSelectOption,
  InterfaceSelectOptionGroup,
} from '@tiendanube/components';
import { OptionType } from 'commons/components/MultiSelect/multiSelectStyleConfig';
import { dateFormat, Format, ninetyDaysAgo } from 'commons/utils/date';
import { METAFIELD_FILTER_PREFIX } from 'domains/Orders/constants';
import useGetOrdersFilters from 'domains/Orders/Orders/hooks/useGetOrdersFilters';
import {
  ExportFiltersType,
  FilterStatusEnum,
  fulfillmentStatusFilter,
  paymentStatusFilter,
  shippingMethodFilter,
  statusFilter,
  PIPE_SEPARATOR,
  COMMA_SEPARATOR,
  paymentProviderFilter,
  paymentMethodsFilter,
  originFilter,
} from 'domains/Orders/Orders/ordersService';
import { getOrdersAggregations } from 'domains/Orders/Orders/ordersSlice';
import { useFilterOptionsSelects } from 'domains/Orders/Orders/pages/OrderListPage/components/OrderListFilter/FilterModal/hooks';
import {
  getPaymentMethodChips,
  getPaymentProviderChips,
  getShippingMethodChips,
  getStatusChips,
} from 'domains/Orders/Orders/utils/OrderListFilter';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import { useFilterSelectedProducts } from '../../../OrderListPage/hooks/useFilterSelectedProducts';

interface DateRangeValues {
  dateFrom: string;
  dateTo: string;
}

interface UseOrdersExportFiltersResult {
  values: ExportFiltersType;
  fulfillmentStatusOptions: InterfaceSelectOptionGroup[];
  paymentOptions: InterfaceSelectOptionGroup[];
  paymentMethodOptions: InterfaceSelectOption[];
  paymentMethodOptionsForMultiSelect: OptionType[];
  paymentProviderOptions: InterfaceSelectOption[];
  paymentProviderOptionsForMultiSelect: OptionType[];
  defaultStatus: OptionType[];
  statusOptionsForMultiSelect: OptionType[];
  defaultShippingMethod: OptionType[];
  shippingMethodOptionsForMultiSelect: GroupBase<OptionType>[];
  defaultFulfillmentStatus: OptionType[];
  defaultPaymentStatus: OptionType[];
  defaultPaymentMethod: OptionType[];
  defaultPaymentProvider: OptionType[];
  defaultOrigin: OptionType[];
  originOptionsForMultiSelect: OptionType[];
  getFilterChips: () => { id: string; label: string; notRemovable?: boolean }[];
  handleChange: ({ name, value }: InterfaceNameValue) => void;
  handleDateRangeChange: ({ dateFrom, dateTo }: DateRangeValues) => void;
  handleMultiSelectChange: (
    name: string,
    selectedValues: MultiValue<{
      label: string;
      value: string;
    }>,
  ) => void;
  setAllValues: (filters: ExportFiltersType) => void;
  applicationOptions: InterfaceSelectOption[];
}

function useOrdersExportFilters(
  filters: ExportFiltersType,
  couponOptions: OptionType[] = [],
): UseOrdersExportFiltersResult {
  const t = useTranslationOrders();
  const aggregations = useSelector(getOrdersAggregations);

  const [values, setValues] = useState(filters);
  const {
    paymentMethodOptions,
    paymentProviderOptions,
    paymentProvidersOrdersFilters,
    applicationOptions,
    applicationOrdersFilters,
  } = useGetOrdersFilters();

  const { deserializeProducts } = useFilterSelectedProducts(
    filters.products,
    aggregations,
  );

  // Use shared hook for all filter options (including shipping methods and status options)
  const {
    defaultStatus,
    statusOptionsForMultiSelect,
    defaultShippingMethod,
    shippingMethodOptionsForMultiSelect,
    defaultFulfillmentStatus,
    fulfillmentStatusOptions,
    defaultPaymentStatus,
    paymentOptions,
    defaultPaymentMethod,
    paymentMethodOptionsForMultiSelect,
    defaultPaymentProvider,
    paymentProviderOptionsForMultiSelect,
    defaultOrigin,
    originOptionsForMultiSelect,
    shippingMethods,
  } = useFilterOptionsSelects({
    values,
    paymentMethodOptions,
    paymentProviderOptions,
  });

  const handleChange = useCallback(
    ({ name, value }) => {
      setValues({ ...values, [name]: value });
    },
    [values],
  );

  const handleDateRangeChange = useCallback(
    ({ dateFrom, dateTo }) => {
      setValues({ ...values, dateFrom, dateTo });
    },
    [values],
  );

  const handleMultiSelectChange = useCallback(
    (name, selectedValues) => {
      let fieldValue: string;

      if (name === 'status') {
        fieldValue =
          selectedValues.length === 0
            ? FilterStatusEnum.ALL
            : selectedValues.map(({ value }) => value).join(',');
      } else {
        // Use pipe separator only for payment provider to avoid conflicts with commas in provider IDs
        const separator =
          name === paymentProviderFilter ? PIPE_SEPARATOR : COMMA_SEPARATOR;
        fieldValue = selectedValues.map(({ value }) => value).join(separator);
      }

      setValues({
        ...values,
        [name]: fieldValue,
      });
    },
    [values],
  );

  const setAllValues = useCallback((filters: ExportFiltersType) => {
    setValues(filters);
  }, []);

  const buildCouponLabel = (
    val: string,
    couponOptions: OptionType[],
    key: string,
  ): { id: string; label: string } | undefined => {
    const opt = couponOptions.find((o) => o.value.toString() === val);
    if (!opt) return undefined;

    return {
      id: `${key}-${val}`,
      label: opt.label,
    };
  };

  const getFilterChips = useCallback(() => {
    const filterChips = Object.entries(filters)
      .filter(
        ([key, value]) =>
          ((value !== '' && value !== FilterStatusEnum.ALL) ||
            (key === statusFilter && value === FilterStatusEnum.ALL)) &&
          key !== 'exactProductSet',
      )
      .filter(([key, _]) => !key.startsWith(METAFIELD_FILTER_PREFIX))
      .flatMap(([key, value]) => {
        if (key === statusFilter) {
          return getStatusChips(value as string, key, t);
        }
        if (key === shippingMethodFilter) {
          return getShippingMethodChips(value as string, key, shippingMethods);
        }
        if (key === paymentProviderFilter && paymentProvidersOrdersFilters) {
          return getPaymentProviderChips(
            value as string,
            key,
            t,
            paymentProvidersOrdersFilters,
          );
        }
        if (key === 'dateFrom') {
          const label = t('appliedFilters.dateFrom', {
            dateFrom: dateFormat(value, Format.DAY_MONTH_YEAR_NUMBER),
          });
          const hasFiltersWithDateRestrictionsApplied =
            filters.paymentMethods !== '' ||
            filters.paymentProvider !== '' ||
            filters.appId !== '';
          const isRemovable =
            !hasFiltersWithDateRestrictionsApplied || value > ninetyDaysAgo;
          return { id: key, label: label, notRemovable: !isRemovable };
        }
        if (key === 'dateTo') {
          const label = t('appliedFilters.dateTo', {
            dateTo: dateFormat(value, Format.DAY_MONTH_YEAR_NUMBER),
          });
          return { id: key, label: label };
        }
        if (key === paymentMethodsFilter) {
          return getPaymentMethodChips(value as string, key, t);
        }
        if (key === 'appId' && applicationOrdersFilters) {
          const label = Object.values(applicationOrdersFilters)
            .flat()
            .find(({ id }) => id === value)?.name;
          return { id: key, label: label ?? '' };
        }
        if (key === paymentStatusFilter || key === fulfillmentStatusFilter) {
          return value.split(',').map((val) => ({
            id: `${key}-${val}`,
            label: t(`appliedFilters.${val}`, val as string),
          }));
        }
        if (key === originFilter) {
          return value
            .toString()
            .split(',')
            .map((origin) => ({
              id: `${key}-${origin.trim()}`,
              label: t(`filters.origin.${origin.trim()}`),
            }));
        }
        if (key === 'products') {
          const deserializedProducts = deserializeProducts(value);

          // When there are no aggregations.variants, this returns undefined
          return aggregations['variants']
            ?.map((product) => {
              if (
                deserializedProducts.find(
                  (dp) => dp.variantId === product.key.toString(),
                )
              ) {
                return product;
              }
              return undefined;
            })
            .filter((p) => !!p && !!p.label)
            .map((product) => {
              // Product should always be set but typescript check doesn't realize it
              if (!product) {
                return { id: '', label: '' };
              }
              return {
                id: `variant-${product.key}`,
                label: product.label,
                badgeCount: product.total,
              };
            });
        }
        if (key === 'couponIdsRaw') {
          const rawValue = (value as string) || '';
          if (couponOptions.length === 0) return [];

          const result = rawValue
            .split(',')
            .map((val) => buildCouponLabel(val, couponOptions, key))
            .filter((f): f is { id: string; label: string } => f !== undefined);

          return Array.isArray(result) ? result : [];
        }

        if (key === 'stockIssues' && value === 'true') {
          return {
            id: key,
            label: t('appliedFilters.stockIssues'),
          };
        }

        return {
          id: key,
          label: t(`appliedFilters.${value}`, value),
        };
      })
      .filter((p) => !!p);

    return filterChips;
  }, [
    filters,
    paymentProvidersOrdersFilters,
    applicationOrdersFilters,
    aggregations,
    deserializeProducts,
    t,
    couponOptions,
    shippingMethods,
  ]);

  return {
    values,
    fulfillmentStatusOptions,
    paymentOptions,
    paymentMethodOptions,
    paymentMethodOptionsForMultiSelect,
    paymentProviderOptions,
    paymentProviderOptionsForMultiSelect,
    defaultStatus,
    statusOptionsForMultiSelect,
    defaultShippingMethod,
    shippingMethodOptionsForMultiSelect,
    defaultFulfillmentStatus,
    defaultPaymentStatus,
    defaultPaymentMethod,
    defaultPaymentProvider,
    defaultOrigin,
    originOptionsForMultiSelect,
    getFilterChips,
    handleChange,
    handleDateRangeChange,
    handleMultiSelectChange,
    setAllValues,
    applicationOptions,
  };
}

export default useOrdersExportFilters;
